/** Per-roof state + payload helpers for multi-roof estimates. */

export const DEFAULT_OVERHEAD_PCT = 0.10;
export const DEFAULT_PROFIT_PCT = 0.50;

export type SteepRoofState = {
    id: string;
    label: string;
    systemId: string;
    area: number;
    edge: number;
    ridge: number;
    valley: number;
    parapet: number;
    plumbingProjections: number;
    ventilationProjections: number;
    woodPlywoodSheets: number;
    woodLumberPieces: number;
    shingleUnderlaymentId: string;
    tileUnderlaymentId: string;
    tileTypeId: string;
    tileFlashingMetal: string;
    tileFlashingSize: string;
    metalUnderlaymentId: string;
    metalTypeId: string;
    overhead_pct: number;
    profit_pct: number;
};

export type FlatRoofState = {
    id: string;
    label: string;
    systemId: string;
    substrate: string;
    area: number;
    edge: number;
    parapet: number;
    scuppers: number;
    drains: number;
    plumbingProjections: number;
    ventilationProjections: number;
    woodPlywoodSheets: number;
    woodLumberPieces: number;
    builtupInsulationId: string;
    builtupTaperedInsId: string;
    builtupTaperedIsoQty: number | null;
    builtupFlatIsoQty: number | null;
    builtupR20Qty: number;
    builtupSmoothQty: number;
    builtupSm15Qty: number;
    builtupCapId: string;
    builtupEdgeMetal: string;
    builtupEdgeSize: string;
    builtupParapetMetal: string;
    builtupParapetCoping: boolean;
    polyglassIpQty: number;
    polyglassEdgeMetal: string;
    polyglassEdgeSize: string;
    wpSbWarranty: string;
    tpoAttachment: string;
    tpoCoverboard: boolean;
    tpoFlatBaseInsId: string;
    tpoInsulationId: string;
    tpoEdgeMetal: string;
    tpoEdgeSize: string;
    tpoParapetMetal: string;
    tpoParapetCoping: boolean;
    tpoTaperedIsoQty: number | null;
    tpoFlatIsoQty: number | null;
    overhead_pct: number;
    profit_pct: number;
};

let _roofSeq = 0;
export function newRoofId(prefix: string): string {
    _roofSeq += 1;
    return `${prefix}-${Date.now()}-${_roofSeq}`;
}

export function defaultSteepRoof(label = 'Steep Roof 1'): SteepRoofState {
    return {
        id: newRoofId('steep'),
        label,
        systemId: 'SHINGLE',
        area: 0,
        edge: 0,
        ridge: 0,
        valley: 0,
        parapet: 0,
        plumbingProjections: 0,
        ventilationProjections: 0,
        woodPlywoodSheets: 5,
        woodLumberPieces: 5,
        shingleUnderlaymentId: 'SHINGLE-UL-1',
        tileUnderlaymentId: 'TILE-UL-1',
        tileTypeId: 'TILE-TYPE-CONC',
        tileFlashingMetal: 'GAL',
        tileFlashingSize: '3X3',
        metalUnderlaymentId: 'METAL-UL-1PLY',
        metalTypeId: 'GAL',
        overhead_pct: DEFAULT_OVERHEAD_PCT,
        profit_pct: DEFAULT_PROFIT_PCT,
    };
}

export function defaultFlatRoof(label = 'Flat Roof 1'): FlatRoofState {
    return {
        id: newRoofId('flat'),
        label,
        systemId: 'NONE-FLAT',
        substrate: 'WOOD',
        area: 0,
        edge: 0,
        parapet: 0,
        scuppers: 0,
        drains: 0,
        plumbingProjections: 0,
        ventilationProjections: 0,
        woodPlywoodSheets: 5,
        woodLumberPieces: 5,
        builtupInsulationId: '',
        builtupTaperedInsId: '',
        builtupTaperedIsoQty: null,
        builtupFlatIsoQty: null,
        builtupR20Qty: 1,
        builtupSmoothQty: 0,
        builtupSm15Qty: 0,
        builtupCapId: 'BUILDUP-CAP-STD',
        builtupEdgeMetal: 'GAL',
        builtupEdgeSize: '3X3',
        builtupParapetMetal: 'GAL',
        builtupParapetCoping: false,
        polyglassIpQty: 1,
        polyglassEdgeMetal: 'GAL',
        polyglassEdgeSize: '3X3',
        wpSbWarranty: '10',
        tpoAttachment: 'HM',
        tpoCoverboard: false,
        tpoFlatBaseInsId: '',
        tpoInsulationId: 'TAPERED-4',
        tpoEdgeMetal: 'GAL',
        tpoEdgeSize: '3X3',
        tpoParapetMetal: 'GAL',
        tpoParapetCoping: false,
        tpoTaperedIsoQty: null,
        tpoFlatIsoQty: null,
        overhead_pct: DEFAULT_OVERHEAD_PCT,
        profit_pct: DEFAULT_PROFIT_PCT,
    };
}

function legacyMarginFromDraft(draft: Record<string, unknown> | null | undefined) {
    const inputs = draft?.inputs as Record<string, number> | undefined;
    return {
        overhead_pct: inputs?.overhead_pct ?? DEFAULT_OVERHEAD_PCT,
        profit_pct: inputs?.profit_pct ?? DEFAULT_PROFIT_PCT,
    };
}

function withRoofMargin<T extends { overhead_pct?: number; profit_pct?: number }>(
    roof: T,
    draft: Record<string, unknown> | null | undefined,
): T & { overhead_pct: number; profit_pct: number } {
    const legacy = legacyMarginFromDraft(draft);
    return {
        ...roof,
        overhead_pct: roof.overhead_pct ?? legacy.overhead_pct,
        profit_pct: roof.profit_pct ?? legacy.profit_pct,
    };
}

/** Migrate legacy single-roof draft → arrays. */
export function steepRoofsFromDraft(draft: Record<string, unknown> | null): SteepRoofState[] {
    if (draft?.steepRoofs && Array.isArray(draft.steepRoofs) && draft.steepRoofs.length > 0) {
        return (draft.steepRoofs as SteepRoofState[]).map(r => withRoofMargin(r, draft));
    }
    const r = defaultSteepRoof('Steep Roof 1');
    r.systemId = (draft?.steepSystemId as string) ?? 'SHINGLE';
    r.shingleUnderlaymentId = (draft?.shingleUnderlaymentId as string) ?? r.shingleUnderlaymentId;
    r.tileUnderlaymentId = (draft?.tileUnderlaymentId as string) ?? r.tileUnderlaymentId;
    r.tileTypeId = (draft?.tileTypeId as string) ?? r.tileTypeId;
    r.tileFlashingMetal = (draft?.tileFlashingMetal as string) ?? r.tileFlashingMetal;
    r.tileFlashingSize = (draft?.tileFlashingSize as string) ?? r.tileFlashingSize;
    r.metalUnderlaymentId = (draft?.metalUnderlaymentId as string) ?? r.metalUnderlaymentId;
    r.metalTypeId = (draft?.metalTypeId as string) ?? r.metalTypeId;
    const inputs = draft?.inputs as Record<string, number> | undefined;
    if (inputs) {
        r.area = inputs.steep_area ?? 0;
        r.edge = inputs.steep_edge ?? 0;
        r.ridge = inputs.ridge ?? 0;
        r.valley = inputs.valley ?? 0;
        r.parapet = inputs.steep_parapet ?? 0;
        r.plumbingProjections = inputs.plumbing_projections ?? 0;
        r.ventilationProjections = inputs.ventilation_projections ?? 0;
        r.woodPlywoodSheets = inputs.wood_plywood_sheets ?? 5;
        r.woodLumberPieces = inputs.wood_lumber_pieces ?? 5;
    }
    return [withRoofMargin(r, draft)];
}

export function flatRoofsFromDraft(draft: Record<string, unknown> | null): FlatRoofState[] {
    if (draft?.flatRoofs && Array.isArray(draft.flatRoofs) && draft.flatRoofs.length > 0) {
        return (draft.flatRoofs as FlatRoofState[]).map(r => withRoofMargin(r, draft));
    }
    const r = defaultFlatRoof('Flat Roof 1');
    r.systemId = (draft?.flatSystemId as string) ?? 'NONE-FLAT';
    r.substrate = (draft?.flatSubstrate as string) ?? 'WOOD';
    r.scuppers = (draft?.flatScuppers as number) ?? 0;
    r.drains = (draft?.flatDrains as number) ?? 0;
    r.builtupInsulationId = (draft?.builtupInsulationId as string) ?? '';
    r.builtupTaperedInsId = (draft?.builtupTaperedInsId as string) ?? '';
    r.builtupTaperedIsoQty = (draft?.builtupTaperedIsoQty as number | null) ?? null;
    r.builtupFlatIsoQty = (draft?.builtupFlatIsoQty as number | null) ?? null;
    r.builtupR20Qty = (draft?.builtupR20Qty as number) ?? 1;
    r.builtupSmoothQty = (draft?.builtupSmoothQty as number) ?? 0;
    r.builtupSm15Qty = (draft?.builtupSm15Qty as number) ?? 0;
    r.builtupCapId = (draft?.builtupCapId as string) ?? r.builtupCapId;
    r.builtupEdgeMetal = (draft?.builtupEdgeMetal as string) ?? 'GAL';
    r.builtupEdgeSize = (draft?.builtupEdgeSize as string) ?? '3X3';
    r.builtupParapetMetal = (draft?.builtupParapetMetal as string) ?? 'GAL';
    r.builtupParapetCoping = (draft?.builtupParapetCoping as boolean) ?? false;
    r.polyglassIpQty = (draft?.polyglassIpQty as number) ?? 1;
    r.polyglassEdgeMetal = (draft?.polyglassEdgeMetal as string) ?? 'GAL';
    r.polyglassEdgeSize = (draft?.polyglassEdgeSize as string) ?? '3X3';
    r.wpSbWarranty = (draft?.wpSbWarranty as string) ?? '10';
    r.tpoAttachment = (draft?.tpoAttachment as string) ?? 'HM';
    r.tpoCoverboard = (draft?.tpoCoverboard as boolean) ?? false;
    r.tpoFlatBaseInsId = (draft?.tpoFlatBaseInsId as string) ?? '';
    r.tpoInsulationId = (draft?.tpoInsulationId as string) ?? 'TAPERED-4';
    r.tpoEdgeMetal = (draft?.tpoEdgeMetal as string) ?? 'GAL';
    r.tpoEdgeSize = (draft?.tpoEdgeSize as string) ?? '3X3';
    r.tpoParapetMetal = (draft?.tpoParapetMetal as string) ?? 'GAL';
    r.tpoParapetCoping = (draft?.tpoParapetCoping as boolean) ?? false;
    r.tpoTaperedIsoQty = (draft?.tpoTaperedIsoQty as number | null) ?? null;
    r.tpoFlatIsoQty = (draft?.tpoFlatIsoQty as number | null) ?? null;
    const inputs = draft?.inputs as Record<string, number> | undefined;
    if (inputs) {
        r.area = inputs.flat_area ?? 0;
        r.edge = inputs.flat_edge ?? 0;
        r.parapet = inputs.flat_parapet ?? 0;
        r.plumbingProjections = inputs.plumbing_projections ?? 0;
        r.ventilationProjections = inputs.ventilation_projections ?? 0;
        r.woodPlywoodSheets = inputs.wood_plywood_sheets ?? 5;
        r.woodLumberPieces = inputs.wood_lumber_pieces ?? 5;
    }
    return [withRoofMargin(r, draft)];
}

export function weightedMarginPct(
    roofs: Array<{ overhead_pct: number; profit_pct: number; area: number }>,
): { overhead_pct: number; profit_pct: number } {
    if (roofs.length === 0) {
        return { overhead_pct: DEFAULT_OVERHEAD_PCT, profit_pct: DEFAULT_PROFIT_PCT };
    }
    const weight = roofs.reduce((s, r) => s + r.area, 0);
    if (weight <= 0) {
        const n = roofs.length;
        return {
            overhead_pct: roofs.reduce((s, r) => s + r.overhead_pct, 0) / n,
            profit_pct: roofs.reduce((s, r) => s + r.profit_pct, 0) / n,
        };
    }
    return {
        overhead_pct: roofs.reduce((s, r) => s + r.overhead_pct * r.area, 0) / weight,
        profit_pct: roofs.reduce((s, r) => s + r.profit_pct * r.area, 0) / weight,
    };
}

export function resolveFlatBackendSystemId(roof: FlatRoofState): string {
    if (roof.systemId === 'TPO') {
        return `TPO-${roof.tpoAttachment}-${roof.tpoCoverboard ? 'CB' : 'NC'}`;
    }
    if (roof.systemId === 'WATERPROOF-SB') {
        return `WATERPROOF-SB-${roof.wpSbWarranty}`;
    }
    return roof.systemId;
}

export function totalSteepArea(roofs: SteepRoofState[]): number {
    return roofs.reduce((s, r) => s + r.area, 0);
}

export function totalFlatArea(roofs: FlatRoofState[]): number {
    return roofs.reduce((s, r) => s + r.area, 0);
}

/** Sum plywood/lumber only from roof types that are in the estimate (matches showSteep/showFlat). */
export function totalWoodQuantities(
    steepRoofs: Array<{ woodPlywoodSheets: number; woodLumberPieces: number }> | null | undefined,
    flatRoofs: Array<{ woodPlywoodSheets: number; woodLumberPieces: number }> | null | undefined,
    opts: { includeSteep: boolean; includeFlat: boolean },
): { plywoodSheets: number; lumberPieces: number } {
    const roofs = [
        ...(opts.includeSteep ? (steepRoofs ?? []) : []),
        ...(opts.includeFlat ? (flatRoofs ?? []) : []),
    ];
    return {
        plywoodSheets: roofs.reduce((s, r) => s + r.woodPlywoodSheets, 0),
        lumberPieces: roofs.reduce((s, r) => s + r.woodLumberPieces, 0),
    };
}

/** Bind active steep roof fields to the legacy scalar names used in EstimatorPage JSX. */
export function steepScalarBindings(
    roof: SteepRoofState,
    patch: (p: Partial<SteepRoofState>) => void,
) {
    return {
        steepSystemId: roof.systemId,
        setSteepSystemId: (systemId: string) => patch({ systemId }),
        shingleUnderlaymentId: roof.shingleUnderlaymentId,
        setShingleUnderlaymentId: (shingleUnderlaymentId: string) => patch({ shingleUnderlaymentId }),
        tileUnderlaymentId: roof.tileUnderlaymentId,
        setTileUnderlaymentId: (tileUnderlaymentId: string) => patch({ tileUnderlaymentId }),
        tileTypeId: roof.tileTypeId,
        setTileTypeId: (tileTypeId: string) => patch({ tileTypeId }),
        tileFlashingMetal: roof.tileFlashingMetal,
        setTileFlashingMetal: (tileFlashingMetal: string) => patch({ tileFlashingMetal }),
        tileFlashingSize: roof.tileFlashingSize,
        setTileFlashingSize: (tileFlashingSize: string) => patch({ tileFlashingSize }),
        metalTypeId: roof.metalTypeId,
        setMetalTypeId: (metalTypeId: string) => patch({ metalTypeId }),
        metalUnderlaymentId: roof.metalUnderlaymentId,
        setMetalUnderlaymentId: (metalUnderlaymentId: string) => patch({ metalUnderlaymentId }),
    };
}

/** Bind active flat roof fields to legacy scalar names. */
export function flatScalarBindings(
    roof: FlatRoofState,
    patch: (p: Partial<FlatRoofState>) => void,
) {
    return {
        flatSystemId: roof.systemId,
        setFlatSystemId: (systemId: string) => patch({ systemId }),
        flatSubstrate: roof.substrate,
        setFlatSubstrate: (substrate: string) => patch({ substrate }),
        flatScuppers: roof.scuppers,
        setFlatScuppers: (scuppers: number) => patch({ scuppers }),
        flatDrains: roof.drains,
        setFlatDrains: (drains: number) => patch({ drains }),
        builtupInsulationId: roof.builtupInsulationId,
        setBuiltupInsulationId: (builtupInsulationId: string) => patch({ builtupInsulationId }),
        builtupTaperedInsId: roof.builtupTaperedInsId,
        setBuiltupTaperedInsId: (builtupTaperedInsId: string) => patch({ builtupTaperedInsId }),
        builtupTaperedIsoQty: roof.builtupTaperedIsoQty,
        setBuiltupTaperedIsoQty: (builtupTaperedIsoQty: number | null) => patch({ builtupTaperedIsoQty }),
        builtupFlatIsoQty: roof.builtupFlatIsoQty,
        setBuiltupFlatIsoQty: (builtupFlatIsoQty: number | null) => patch({ builtupFlatIsoQty }),
        builtupR20Qty: roof.builtupR20Qty,
        setBuiltupR20Qty: (builtupR20Qty: number) => patch({ builtupR20Qty }),
        builtupSmoothQty: roof.builtupSmoothQty,
        setBuiltupSmoothQty: (builtupSmoothQty: number) => patch({ builtupSmoothQty }),
        builtupSm15Qty: roof.builtupSm15Qty,
        setBuiltupSm15Qty: (builtupSm15Qty: number) => patch({ builtupSm15Qty }),
        builtupCapId: roof.builtupCapId,
        setBuiltupCapId: (builtupCapId: string) => patch({ builtupCapId }),
        builtupEdgeMetal: roof.builtupEdgeMetal,
        setBuiltupEdgeMetal: (builtupEdgeMetal: string) => patch({ builtupEdgeMetal }),
        builtupEdgeSize: roof.builtupEdgeSize,
        setBuiltupEdgeSize: (builtupEdgeSize: string) => patch({ builtupEdgeSize }),
        builtupParapetMetal: roof.builtupParapetMetal,
        setBuiltupParapetMetal: (builtupParapetMetal: string) => patch({ builtupParapetMetal }),
        builtupParapetCoping: roof.builtupParapetCoping,
        setBuiltupParapetCoping: (builtupParapetCoping: boolean) => patch({ builtupParapetCoping }),
        polyglassIpQty: roof.polyglassIpQty,
        setPolyglassIpQty: (polyglassIpQty: number) => patch({ polyglassIpQty }),
        polyglassEdgeMetal: roof.polyglassEdgeMetal,
        setPolyglassEdgeMetal: (polyglassEdgeMetal: string) => patch({ polyglassEdgeMetal }),
        polyglassEdgeSize: roof.polyglassEdgeSize,
        setPolyglassEdgeSize: (polyglassEdgeSize: string) => patch({ polyglassEdgeSize }),
        wpSbWarranty: roof.wpSbWarranty,
        setWpSbWarranty: (wpSbWarranty: string) => patch({ wpSbWarranty }),
        tpoAttachment: roof.tpoAttachment,
        setTpoAttachment: (tpoAttachment: string) => patch({ tpoAttachment }),
        tpoCoverboard: roof.tpoCoverboard,
        setTpoCoverboard: (tpoCoverboard: boolean) => patch({ tpoCoverboard }),
        tpoFlatBaseInsId: roof.tpoFlatBaseInsId,
        setTpoFlatBaseInsId: (tpoFlatBaseInsId: string) => patch({ tpoFlatBaseInsId }),
        tpoInsulationId: roof.tpoInsulationId,
        setTpoInsulationId: (tpoInsulationId: string) => patch({ tpoInsulationId }),
        tpoEdgeMetal: roof.tpoEdgeMetal,
        setTpoEdgeMetal: (tpoEdgeMetal: string) => patch({ tpoEdgeMetal }),
        tpoEdgeSize: roof.tpoEdgeSize,
        setTpoEdgeSize: (tpoEdgeSize: string) => patch({ tpoEdgeSize }),
        tpoParapetMetal: roof.tpoParapetMetal,
        setTpoParapetMetal: (tpoParapetMetal: string) => patch({ tpoParapetMetal }),
        tpoParapetCoping: roof.tpoParapetCoping,
        setTpoParapetCoping: (tpoParapetCoping: boolean) => patch({ tpoParapetCoping }),
        tpoTaperedIsoQty: roof.tpoTaperedIsoQty,
        setTpoTaperedIsoQty: (tpoTaperedIsoQty: number | null) => patch({ tpoTaperedIsoQty }),
        tpoFlatIsoQty: roof.tpoFlatIsoQty,
        setTpoFlatIsoQty: (tpoFlatIsoQty: number | null) => patch({ tpoFlatIsoQty }),
    };
}

export function steepRoofToApi(roof: SteepRoofState, metalTypeSystemId: string) {
    return {
        id: roof.id,
        label: roof.label,
        system_id: roof.systemId,
        area: roof.area,
        edge: roof.edge,
        ridge: roof.ridge,
        valley: roof.valley,
        parapet: roof.parapet,
        plumbing_projections: roof.plumbingProjections,
        ventilation_projections: roof.ventilationProjections,
        wood_plywood_sheets: roof.woodPlywoodSheets,
        wood_lumber_pieces: roof.woodLumberPieces,
        shingle_underlayment_id: roof.shingleUnderlaymentId,
        tile_underlayment_id: roof.tileUnderlaymentId,
        tile_type_id: roof.tileTypeId,
        tile_flashing_id: `FLASH-${roof.tileFlashingMetal}-${roof.tileFlashingSize}`,
        metal_underlayment_id: roof.metalUnderlaymentId,
        metal_type_id: metalTypeSystemId,
        overhead_pct: roof.overhead_pct,
        profit_pct: roof.profit_pct,
    };
}

export function flatRoofToApi(
    roof: FlatRoofState,
    resolvedTaperedIsoQty: number,
    resolvedFlatIsoQty: number,
) {
    return {
        id: roof.id,
        label: roof.label,
        system_id: resolveFlatBackendSystemId(roof),
        substrate: roof.substrate,
        area: roof.area,
        edge: roof.edge,
        parapet: roof.parapet,
        scuppers: roof.scuppers,
        drains: roof.drains,
        plumbing_projections: roof.plumbingProjections,
        ventilation_projections: roof.ventilationProjections,
        wood_plywood_sheets: roof.woodPlywoodSheets,
        wood_lumber_pieces: roof.woodLumberPieces,
        buildup_r20_qty: roof.builtupR20Qty,
        buildup_smooth_qty: roof.builtupSmoothQty,
        buildup_sm15_qty: roof.builtupSm15Qty,
        buildup_cap_id: roof.builtupCapId,
        buildup_insulation_id: roof.builtupInsulationId,
        buildup_edge_id: `BUILDUP-EDGE-${roof.builtupEdgeMetal}-${roof.builtupEdgeSize}`,
        buildup_tapered_ins_id: roof.builtupTaperedInsId,
        buildup_tapered_iso_qty: resolvedTaperedIsoQty,
        buildup_flat_iso_qty: resolvedFlatIsoQty,
        polyglass_ip_qty: roof.polyglassIpQty,
        polyglass_edge_id: `BUILDUP-EDGE-${roof.polyglassEdgeMetal}-${roof.polyglassEdgeSize}`,
        tpo_flat_base_ins_id: roof.tpoFlatBaseInsId,
        tpo_tapered_iso_qty: resolvedTaperedIsoQty,
        tpo_flat_iso_qty: resolvedFlatIsoQty,
        tpo_edge_id: `BUILDUP-EDGE-${roof.tpoEdgeMetal}-${roof.tpoEdgeSize}`,
        tpo_parapet_metal: roof.tpoParapetMetal,
        tpo_parapet_coping: roof.tpoParapetCoping,
        overhead_pct: roof.overhead_pct,
        profit_pct: roof.profit_pct,
    };
}
